/**
 * Cloudflare's Turnstile script, shared by the in-page widget and the bridge page.
 *
 * Both render the same widget — same site key, same action, same callbacks — and differ
 * only in which document it lives in (see `turnstile-bridge.ts`). Keeping the loader and
 * the render options here stops the two from drifting apart.
 */

const SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const ACTION = "otp_request";

interface TurnstileApi {
  render: (element: HTMLElement, options: Record<string, unknown>) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** The public site key baked into this build, if it has one. */
export function turnstileSiteKey(): string | undefined {
  return (
    (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) || undefined
  );
}

/** Load Cloudflare's script once, shared across mounts. */
export function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Turnstile failed to load")),
      );
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () =>
      reject(new Error("Turnstile failed to load")),
    );
    document.head.appendChild(script);
  });
}

/**
 * Render the widget into `element` once the script has loaded.
 *
 * `onToken` receives each new token, and `null` whenever the current one stops being
 * usable — it expired, or the challenge errored. The API fails closed, so a missing
 * token is rejected rather than waved through.
 *
 * @returns The widget id, for `window.turnstile.remove`.
 */
export function renderTurnstile(
  element: HTMLElement,
  {
    theme,
    onToken,
  }: { theme: "light" | "dark"; onToken: (token: string | null) => void },
): string | undefined {
  const sitekey = turnstileSiteKey();
  if (!sitekey || !window.turnstile) return undefined;
  return window.turnstile.render(element, {
    sitekey,
    action: ACTION,
    theme,
    callback: (token: string) => onToken(token),
    "expired-callback": () => onToken(null),
    "error-callback": () => onToken(null),
  });
}
